/**
 * Contextual shimmer skeletons for the Unified Inbox.
 * Pure layout placeholders — no user-facing copy (i18n ratchet).
 */
import { Skeleton, SkeletonAvatar } from '../../../components/ui/Skeleton';

/** One conversation-row placeholder matching ConversationList item layout. */
export function ConversationRowSkeleton(): JSX.Element {
  return (
    <div className="flex items-start gap-3 p-3" aria-hidden>
      <SkeletonAvatar size="md" />
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <Skeleton className="h-3.5 w-28" />
          <Skeleton className="h-3 w-8" />
        </div>
        <Skeleton className="h-3 w-full max-w-[11rem]" />
        <Skeleton className="h-4 w-16 rounded" />
      </div>
    </div>
  );
}

/** Conversation list while the first page loads. */
export function ConversationListSkeleton({ rows = 6 }: { rows?: number }): JSX.Element {
  return (
    <div className="divide-y divide-gray-100" role="status" aria-busy="true" aria-label="Loading conversations">
      {Array.from({ length: rows }, (_, i) => (
        <ConversationRowSkeleton key={i} />
      ))}
    </div>
  );
}

/** Message bubbles while a thread’s history loads. */
export function ChatThreadSkeleton(): JSX.Element {
  return (
    <div
      className="flex-1 overflow-hidden p-4 bg-gray-50"
      role="status"
      aria-busy="true"
      aria-label="Loading messages"
    >
      <div className="space-y-3 max-w-3xl mx-auto">
        <div className="flex justify-start">
          <Skeleton className="h-10 w-[55%] max-w-xs rounded-lg" />
        </div>
        <div className="flex justify-end">
          <Skeleton className="h-10 w-[45%] max-w-xs rounded-lg" />
        </div>
        <div className="flex justify-start">
          <Skeleton className="h-14 w-[60%] max-w-sm rounded-lg" />
        </div>
        <div className="flex justify-end">
          <Skeleton className="h-8 w-[40%] max-w-xs rounded-lg" />
        </div>
        <div className="flex justify-start">
          <Skeleton className="h-10 w-[50%] max-w-xs rounded-lg" />
        </div>
      </div>
    </div>
  );
}

/** Empty-panel placeholder before a conversation is selected (desktop). */
export function ChatPanelIdleSkeleton(): JSX.Element {
  return (
    <div className="flex-1 flex flex-col" aria-hidden>
      <div className="p-4 border-b border-gray-200 flex items-center gap-3">
        <SkeletonAvatar size="md" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
      <ChatThreadSkeleton />
      <div className="p-3 border-t border-gray-200 flex items-center gap-2">
        <Skeleton className="h-9 flex-1 rounded-lg" />
        <Skeleton className="h-9 w-9 rounded-full" />
      </div>
    </div>
  );
}

/** Customer drawer metrics/contact shimmer. */
export function CustomerDrawerSkeleton(): JSX.Element {
  return (
    <div className="p-4 space-y-5" role="status" aria-busy="true" aria-label="Loading contact">
      <div className="space-y-3">
        <Skeleton className="h-3 w-16" />
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-3 space-y-2">
              <Skeleton className="h-3 w-10" />
              <Skeleton className="h-5 w-14" />
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Skeleton className="h-3 w-14" />
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-4 w-48" />
      </div>
    </div>
  );
}

/** Template picker list while approved templates load. */
export function TemplatePickerSkeleton({ rows = 3 }: { rows?: number }): JSX.Element {
  return (
    <div className="space-y-2 py-2" role="status" aria-busy="true" aria-label="Loading templates">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="rounded-lg border border-gray-100 p-3 space-y-2" aria-hidden>
          <Skeleton className="h-3.5 w-40" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-4/5" />
        </div>
      ))}
    </div>
  );
}
